import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';
import { DEFAULT_IMAGE_MODAL_MIN_SCALE } from './imageModalZoomTypes';

/**
 * Centre point of the viewport in viewport-local coordinates.
 */
export const getViewportCentrePoint = (viewportSize: RectSize): Point => ({
    x: viewportSize.width / 2,
    y: viewportSize.height / 2,
});

/** Slack for treating an anchor or translation as being at the centre/rest position. */
const ZOOM_ANCHOR_TOLERANCE = 1;

/**
 * Returns whether the translation is at the centred rest position.
 */
export const isZeroTranslation = (translation: Point): boolean =>
    Math.abs(translation.x) <= ZOOM_ANCHOR_TOLERANCE && Math.abs(translation.y) <= ZOOM_ANCHOR_TOLERANCE;

/**
 * Returns whether the anchor point is the viewport centre.
 */
export const isViewportCentreAnchor = (anchorPoint: Point, viewportSize: RectSize): boolean => {
    const centre = getViewportCentrePoint(viewportSize);

    return (
        Math.abs(anchorPoint.x - centre.x) <= ZOOM_ANCHOR_TOLERANCE &&
        Math.abs(anchorPoint.y - centre.y) <= ZOOM_ANCHOR_TOLERANCE
    );
};

/**
 * Interpolates pan back to zero in sync with zoom-out progress.
 */
export const getRestZoomOutPan = ({
    startScale,
    startPan,
    nextScale,
    minScale = DEFAULT_IMAGE_MODAL_MIN_SCALE,
}: {
    startScale: number;
    startPan: Point;
    nextScale: number;
    minScale?: number;
}): Point => {
    const scaleRange = startScale - minScale;
    if (scaleRange <= 0 || nextScale <= minScale) {
        return { x: 0, y: 0 };
    }

    const scaleProgress = (nextScale - minScale) / scaleRange;

    return {
        x: startPan.x * scaleProgress,
        y: startPan.y * scaleProgress,
    };
};

/**
 * Adjusts pan so the viewport anchor point stays on the same content pixel while the scale layer zooms.
 */
export const getCursorAnchoredPanForScale = ({
    anchorPoint,
    viewportSize,
    currentScale,
    currentPan,
    nextScale,
}: {
    anchorPoint: Point;
    viewportSize: RectSize;
    currentScale: number;
    currentPan: Point;
    nextScale: number;
}): Point => {
    if (!currentScale) return currentPan;

    const centre = getViewportCentrePoint(viewportSize);

    return {
        x: anchorPoint.x - centre.x - (nextScale * (anchorPoint.x - centre.x - currentPan.x)) / currentScale,
        y: anchorPoint.y - centre.y - (nextScale * (anchorPoint.y - centre.y - currentPan.y)) / currentScale,
    };
};

/**
 * Calculates the next pan offset for a scale change using the V2 split-transform model.
 */
export const getZoomTranslationForScale = ({
    anchorPoint,
    currentScale,
    currentTranslation,
    nextScale,
    viewportSize,
    restZoomOutStart,
}: {
    anchorPoint: Point;
    currentScale: number;
    currentTranslation: Point;
    nextScale: number;
    viewportSize: RectSize;
    restZoomOutStart?: { startScale: number; startPan: Point };
}): Point => {
    if (!currentScale) return currentTranslation;

    if (isZeroTranslation(currentTranslation)) {
        if (nextScale <= currentScale) {
            return { x: 0, y: 0 };
        }

        return getCursorAnchoredPanForScale({
            anchorPoint,
            viewportSize,
            currentScale,
            currentPan: currentTranslation,
            nextScale,
        });
    }

    if (isViewportCentreAnchor(anchorPoint, viewportSize) && nextScale < currentScale) {
        const start = restZoomOutStart ?? { startScale: currentScale, startPan: currentTranslation };

        return getRestZoomOutPan({
            startScale: start.startScale,
            startPan: start.startPan,
            nextScale,
        });
    }

    return getCursorAnchoredPanForScale({
        anchorPoint,
        viewportSize,
        currentScale,
        currentPan: currentTranslation,
        nextScale,
    });
};
